import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getActiveTierConfigurations, getActivePromotions, calculateDiscountedPrice } from '../../utils/tierConfigUtils';
import { getSortedTiersByRank, createTierBadge, getHigherTiers } from '../../utils/tierRankingUtils';

const TierUpgradeModal = ({ isOpen, onClose, currentTier, userRole, userId, onUpgradeSuccess }) => {
  const [selectedTier, setSelectedTier] = useState('');
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const [tierConfigs, setTierConfigs] = useState({});
  const [promotions, setPromotions] = useState([]);
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  // ดึงข้อมูล tier configurations และ promotions
  useEffect(() => {
    if (isOpen && userRole) {
      loadTierData();
    }
  }, [isOpen, userRole]);

  const loadTierData = async () => {
    try {
      const [configs, activePromotions] = await Promise.all([
        getActiveTierConfigurations(userRole),
        getActivePromotions(userRole, currentTier)
      ]);
      
      setTierConfigs(configs);
      setPromotions(activePromotions);
    } catch (error) {
      console.error('Error loading tier data:', error);
      alert('เกิดข้อผิดพลาดในการโหลดข้อมูล Tier');
    }
  };

  const availableTiers = tierConfigs;

  const handleUpgrade = async () => {
    if (!selectedTier) return;

    try {
      setLoading(true);
      const userRef = doc(db, 'users', userId);
      const tierExpiryDate = new Date();
      tierExpiryDate.setDate(tierExpiryDate.getDate() + duration);

      await updateDoc(userRef, {
        tier: selectedTier,
        tierExpiryDate: tierExpiryDate.toISOString(),
        tierUpdatedAt: new Date().toISOString(),
        tierUpdatedBy: 'self'
      });

      alert(`อัพเกรด Tier เป็น ${availableTiers[selectedTier]?.name} สำเร็จ!`);
      onUpgradeSuccess && onUpgradeSuccess();
      onClose();
    } catch (error) {
      console.error('Error upgrading tier:', error);
      alert('เกิดข้อผิดพลาดในการอัพเกรด Tier');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">อัพเกรด Tier</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Current Tier */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Tier ปัจจุบัน</h3>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${availableTiers[currentTier]?.color || 'bg-gray-100 text-gray-800'}`}>
              {availableTiers[currentTier]?.name || 'Free'}
            </span>
          </div>

          {/* Tier Selection */}
          <div>
            <h3 className="font-semibold mb-4">เลือก Tier ใหม่</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getHigherTiers(userRole, currentTier, availableTiers).map((tierData) => {
                const badge = createTierBadge(userRole, tierData.tierKey, tierData, 'lg');
                
                return (
                  <button
                    key={tierData.tierKey}
                    onClick={() => setSelectedTier(tierData.tierKey)}
                    className={`p-4 border rounded-lg text-left transition ${
                      selectedTier === tierData.tierKey
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{badge.icon}</span>
                        <div>
                          <div className="font-semibold text-lg">{badge.name}</div>
                          <div className="text-xs text-gray-500">Rank {badge.rank}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">{tierData.price} T Coins/เดือน</div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <ul className="list-disc list-inside space-y-1">
                        {tierData.features.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Promotions */}
          {selectedTier && promotions.length > 0 && (
            <div>
              <h3 className="font-semibold mb-4">🎉 Promotions ที่ใช้งานได้</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setSelectedPromotion(null)}
                  className={`w-full p-3 border rounded-lg text-left transition ${
                    !selectedPromotion
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="font-medium">ไม่ใช้ Promotion</div>
                  <div className="text-sm text-gray-600">ราคาปกติ</div>
                </button>
                {promotions.map((promotion) => (
                  <button
                    key={promotion.id}
                    onClick={() => setSelectedPromotion(promotion)}
                    className={`w-full p-3 border rounded-lg text-left transition ${
                      selectedPromotion?.id === promotion.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{promotion.name}</div>
                        <div className="text-sm text-gray-600">{promotion.description}</div>
                        {promotion.promoCode && (
                          <div className="text-xs text-blue-600 font-mono mt-1">
                            Code: {promotion.promoCode}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-green-600">
                          {promotion.discountType === 'percentage' 
                            ? `ลด ${promotion.discountValue}%`
                            : `ลด ${promotion.discountValue} T Coins`
                          }
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Duration Selection */}
          {selectedTier && (
            <div>
              <h3 className="font-semibold mb-4">เลือกระยะเวลา</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { days: 7, label: '1 สัปดาห์', discount: 0 },
                  { days: 30, label: '1 เดือน', discount: 0 },
                  { days: 90, label: '3 เดือน', discount: 10 },
                  { days: 365, label: '1 ปี', discount: 20 }
                ].map((option) => {
                  const originalPrice = availableTiers[selectedTier]?.price || 0;
                  const discountedPrice = Math.round(originalPrice * (1 - option.discount / 100));
                  
                  return (
                    <button
                      key={option.days}
                      onClick={() => setDuration(option.days)}
                      className={`p-3 border rounded-lg text-center transition ${
                        duration === option.days
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium">{option.label}</div>
                      <div className="text-sm text-gray-600">
                        {discountedPrice} T Coins
                        {option.discount > 0 && (
                          <span className="text-green-600 ml-1">
                            (-{option.discount}%)
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary */}
          {selectedTier && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">สรุปการอัพเกรด</h3>
              <div className="space-y-1 text-sm">
                <div>จาก: {availableTiers[currentTier]?.name || 'Free'}</div>
                <div>เป็น: {availableTiers[selectedTier]?.name}</div>
                <div>ระยะเวลา: {duration} วัน</div>
                {selectedPromotion && (
                  <div className="text-green-600">
                    Promotion: {selectedPromotion.name}
                  </div>
                )}
                <div className="space-y-1">
                  <div>ราคาเดิม: {availableTiers[selectedTier]?.price || 0} T Coins</div>
                  {selectedPromotion && (
                    <div className="text-green-600">
                      ส่วนลด: {selectedPromotion.discountType === 'percentage' 
                        ? `${selectedPromotion.discountValue}%`
                        : `${selectedPromotion.discountValue} T Coins`
                      }
                    </div>
                  )}
                  <div className="font-semibold text-lg">
                    ราคาสุดท้าย: {calculateDiscountedPrice(
                      availableTiers[selectedTier]?.price || 0, 
                      selectedPromotion
                    )} T Coins
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              ยกเลิก
            </button>
            <button
              onClick={handleUpgrade}
              disabled={!selectedTier || loading}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
            >
              {loading ? 'กำลังอัพเกรด...' : 'อัพเกรด Tier'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TierUpgradeModal;
